import React from 'react';
import type { GridCell, GridRow, Milestone } from '../../types/life';
import { cn } from '../../utils/cn';
import { plural } from '../../utils/format';
import { DAY_COLUMNS } from '../../utils/lifeGrid';
import { StrikeRow } from '../StrikeRow';
interface NowViewProps {
  stripRows: GridRow[];
  stripColumns: number;
  dayRows: GridRow[];
  year: number;
  now: Date;
  compact: boolean;
  milestoneByCell: Map<string, Milestone>;
  focusedKey: string | null;
  onSelectYear: (cell: GridCell) => void;
  onSelectMonth: (row: GridRow) => void;
  onSelectDay: (cell: GridCell) => void;
}
export function NowView({
  stripRows,
  stripColumns,
  dayRows,
  year,
  now,
  compact,
  milestoneByCell,
  focusedKey,
  onSelectYear,
  onSelectMonth,
  onSelectDay
}: NowViewProps) {
  const daysLeftInYear = Math.max(0, Math.ceil((new Date(year + 1, 0, 1).getTime() - now.getTime()) / 86_400_000));
  return <div className={compact ? 'space-y-8' : 'space-y-12'} data-id="element-176" data-mp="2h3g6gxukxovf">
      <section aria-label="Годы жизни" className={compact ? 'space-y-3' : 'space-y-4'} data-id="element-177" data-mp="2h62eeyldu6e6">
        {stripRows.map(row => <StrikeRow key={row.key} columns={stripColumns} gapPx={compact ? 8 : 12} labelWidth={0} fraction={row.fraction} data-id="element-178" data-mp="1ts0jkebfucze">
            {row.cells.map(cell => {
          if (cell.state === 'void') return <span key={cell.key} aria-hidden data-id="element-179" data-mp="1ksn9bjfhpkdm" />;
          const milestone = milestoneByCell.get(cell.key);
          const focused = focusedKey === cell.key;
          return <button key={cell.key} type="button" data-cell={cell.key} title={milestone?.title} onClick={() => onSelectYear(cell)} aria-label={milestone ? `${cell.label}, майлстоун: ${milestone.title}` : cell.label} className={cn('relative px-0.5 py-1 font-semibold tracking-tight transition-colors duration-150 ease-swift focus-visible:outline focus-visible:outline-1 focus-visible:outline-accent', compact ? 'text-center text-sm' : 'text-left text-base md:text-lg', cell.state === 'past' && 'text-ink', cell.state === 'future' && 'text-muted hover:text-ink', cell.state === 'current' && 'text-accent', milestone && 'text-accent', focused && 'bg-accent-soft')} data-id="element-180" data-mp="1aupluy5gfrm2">
                  {cell.label}
                  {(milestone || cell.state === 'current') && <span className="bg-accent absolute -right-1 top-0.5 h-1.5 w-1.5 rounded-full" aria-hidden data-id="element-181" data-mp="33o6zsltdnqra" />}
                </button>;
        })}
          </StrikeRow>)}
      </section>

      <section aria-label={`Текущий год: ${year}`} className="space-y-3" data-id="element-182" data-mp="1s88n87yzqmlb">
        <div className="border-line flex items-baseline justify-between gap-4 border-b pb-2" data-id="element-183" data-mp="1n9nlu5805wva">
          <h2 className="text-ink font-mono text-sm" data-id="element-184" data-mp="1d8mqwvavoj0t">{year}</h2>
          <p className="text-muted text-xs" data-id="element-185" data-mp="1i5v6qp8k0ayw">
            осталось <span className="text-accent font-mono tabular-nums" data-id="element-186" data-mp="1i6o9lf4e6gbr">{daysLeftInYear}</span>{' '}
            {plural(daysLeftInYear, ['день', 'дня', 'дней'])}
          </p>
        </div>

        <div className={compact ? 'space-y-1.5' : 'space-y-2'} data-id="element-187" data-mp="1rxr7k820yf6t">
          {dayRows.map(row => {
          const monthMilestone = milestoneByCell.get(row.key);
          const monthFocused = focusedKey === row.key;
          return <StrikeRow key={row.key} columns={DAY_COLUMNS} gapPx={compact ? 3 : 6} labelWidth={compact ? 34 : 52} fraction={row.fraction} thickness={1.5} label={<button type="button" data-cell={row.key} title={monthMilestone?.title} onClick={() => onSelectMonth(row)} aria-label={monthMilestone ? `${row.label} ${year}, майлстоун: ${monthMilestone.title}` : `${row.label} ${year}`} className={cn('relative text-left font-semibold leading-none transition-colors duration-150 ease-swift focus-visible:outline focus-visible:outline-1 focus-visible:outline-accent', compact ? 'text-[11px]' : 'text-sm', monthMilestone ? 'text-accent' : 'text-ink hover:text-accent', monthFocused && 'bg-accent-soft')} data-id="element-189" data-mp="bk13v2ethsxp">
                    {row.label}
                    {monthMilestone && <span className="bg-accent absolute -right-1 top-0 h-1.5 w-1.5 rounded-full" aria-hidden data-id="element-190" data-mp="14i37r0bwkav2" />}
                  </button>} data-id="element-188" data-mp="25vz96ta0a4ob">
                {row.cells.map(cell => {
              if (cell.state === 'void') return <span key={cell.key} aria-hidden data-id="element-191" data-mp="3fywojb47fzov" />;
              const milestone = milestoneByCell.get(cell.key);
              const focused = focusedKey === cell.key;
              const accent = Boolean(milestone) || cell.state === 'current';
              const label = milestone ? `${cell.label} ${row.label}, майлстоун: ${milestone.title}` : `${cell.label} ${row.label}`;

              // на узком экране числа не влезают в 10px колонки — рисуем точки с ровным шагом
              if (compact) {
                return <button key={cell.key} type="button" data-cell={cell.key} onClick={() => onSelectDay(cell)} aria-label={label} className="flex h-4 items-center justify-center focus-visible:outline focus-visible:outline-1 focus-visible:outline-accent" data-id="element-192" data-mp="3pw1elm168fay">
                        <span aria-hidden className={cn('rounded-full', accent ? 'bg-accent' : cell.state === 'past' ? 'bg-ink' : 'bg-faint', focused && 'ring-accent ring-2')} style={{
                    width: accent ? 6 : 4,
                    height: accent ? 6 : 4,
                    boxShadow: accent ? '0 0 0 2px var(--paper)' : undefined
                  }} data-id="element-193" data-mp="1qf9536sonun8" />
                      </button>;
              }
              return <button key={cell.key} type="button" data-cell={cell.key} title={milestone?.title} onClick={() => onSelectDay(cell)} aria-label={label} className={cn('relative py-0.5 text-center text-[11px] tabular-nums transition-colors duration-150 ease-swift focus-visible:outline focus-visible:outline-1 focus-visible:outline-accent', cell.state === 'past' && 'text-ink', cell.state === 'future' && 'text-muted hover:text-ink', cell.state === 'current' && 'text-accent font-semibold', milestone && 'text-accent font-semibold', focused && 'bg-accent-soft')} data-id="element-194" data-mp="2x1mp4848wx1e">
                      {cell.label}
                      {accent && <span className="bg-accent absolute right-0 top-0 h-1 w-1 rounded-full" aria-hidden data-id="element-195" data-mp="5vdo9fpku5jg" />}
                    </button>;
            })}
              </StrikeRow>;
        })}
        </div>
      </section>
    </div>;
}